import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { History, User, FileText, Plus, Upload, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Historico() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/history/recent?limit=50`);
      setEntries(response.data.entries || []);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'created':
        return <Plus className="w-4 h-4 text-green-600" strokeWidth={1.5} />;
      case 'updated':
        return <FileText className="w-4 h-4 text-blue-600" strokeWidth={1.5} />;
      case 'deleted':
        return <FileText className="w-4 h-4 text-red-600" strokeWidth={1.5} />;
      default:
        return <FileText className="w-4 h-4 text-slate-400" strokeWidth={1.5} />;
    }
  };

  const getActionBadge = (action, changes) => {
    if (changes?.source === 'import') {
      return <Badge className="bg-violet-100 text-violet-700 text-xs">Importado</Badge>;
    }
    switch (action) {
      case 'created':
        return <Badge className="bg-green-100 text-green-700 text-xs">Criado</Badge>;
      case 'updated':
        return <Badge className="bg-blue-100 text-blue-700 text-xs">Atualizado</Badge>;
      case 'deleted':
        return <Badge className="bg-red-100 text-red-700 text-xs">Excluído</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-700 text-xs">{action}</Badge>;
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl" data-testid="historico-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Histórico de Alterações
          </h1>
          <p className="text-slate-500 mt-1">
            Acompanhe todas as ações realizadas no sistema
          </p>
        </div>

        {/* History List */}
        <Card className="border-slate-200 shadow-none">
          <CardHeader className="pb-0">
            <CardTitle className="font-heading text-lg font-bold flex items-center gap-2">
              <History className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
              Atividades Recentes
              <Badge variant="secondary" className="ml-2 bg-slate-100 text-slate-600">
                {entries.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : entries.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <History className="w-12 h-12 mx-auto text-slate-300 mb-3" strokeWidth={1.5} />
                <p>Nenhuma atividade registrada</p>
              </div>
            ) : (
              <ScrollArea className="h-[600px]">
                <div className="space-y-3">
                  {entries.map((entry, index) => (
                    <div
                      key={entry.id || index}
                      className="flex items-start gap-4 p-4 rounded-md border border-slate-200 hover:border-blue-200 transition-colors"
                      data-testid={`history-entry-${index}`}
                    >
                      <div className="p-2 bg-slate-50 rounded-md">
                        {getActionIcon(entry.action)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {entry.code && (
                            <code className="material-code text-sm bg-slate-100 px-2 py-0.5 rounded">
                              {entry.code}
                            </code>
                          )}
                          {getActionBadge(entry.action, entry.changes)}
                        </div>
                        {entry.description && (
                          <p className="text-sm text-slate-700 truncate">
                            {entry.description}
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" strokeWidth={1.5} />
                            {entry.user_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" strokeWidth={1.5} />
                            {formatDate(entry.timestamp)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
